import React, { useEffect, useMemo, useState } from "react";
import { Control, UseFormSetValue } from "react-hook-form";
import size from "lodash/size";
import map from "lodash/map";

import { Box, Typography, Button } from "@mui/material";

import AddCreditCardDialog from "components/globals/AddCreditCardDialog";
import PaymentForm from "./components/PaymentForm";
import {
  useCurrentUser,
  useDefaultPassenger,
  useLaunchDarklyFlags,
  useOperator,
} from "globals/hooks";
import RHFSingleSelect from "components/react-hook-form/RHFSingleSelect";
import { getPaymentMethodInfoString } from "globals/utils/helpers";
import PaymentMethodOption from "./components/PaymentMethodOption";
import { useCreateRequestContext } from "pages/new/context/useCreateRequestContext";
import { Request } from "types";
import { getDefaultPaymentMethodId } from "./util";
import type { SavedPaymentMethod } from "components/payment/PaymentMethodSetup/types";
import type {
  ProfileSetupCorrelation,
  ProfileSetupReturn,
} from "components/profile/paymentMethodSetupReturn";

type PaymentMethodBlockProps = {
  control: Control<any>;
  trackingFrom?: "reserve" | "quoteRequest";
  request?: Request;
  setValue: UseFormSetValue<any>;
  paymentElementForm?: React.ReactNode;
  paymentElementEnabled?: boolean;
  paymentElementResuming?: boolean;
  onSavedPaymentMethod?: (method: SavedPaymentMethod) => void;
  savedPaymentMethod?: SavedPaymentMethod;
  paymentElementReturn?: ProfileSetupReturn;
  onBeforePaymentElementConfirm?: (
    correlation: ProfileSetupCorrelation
  ) => void;
  onPaymentElementReturnCleared?: () => void;
  paymentMethodPayerId?: string;
};

function PaymentMethodBlock(props: PaymentMethodBlockProps) {
  const {
    control,
    trackingFrom,
    request,
    setValue,
    paymentElementForm,
    paymentElementEnabled,
    paymentElementResuming,
    onSavedPaymentMethod,
    savedPaymentMethod,
    paymentElementReturn,
    onBeforePaymentElementConfirm,
    onPaymentElementReturnCleared,
    paymentMethodPayerId,
  } = props;

  // hooks
  const {
    operator: { id: operatorId, enableCreditCardWhenBooking },
  } = useOperator();
  const authenticatedContact = useCurrentUser();
  const currentUser =
    paymentMethodPayerId && authenticatedContact?.id !== paymentMethodPayerId
      ? undefined
      : authenticatedContact;
  const creditCardOnFile =
    !paymentElementResuming &&
    !!currentUser &&
    (!!size(currentUser.paymentMethods) || !!savedPaymentMethod);
  const { enableLinkedPassenger } = useLaunchDarklyFlags();
  const [requestContext] = useCreateRequestContext();
  const { selectedPassenger } = useDefaultPassenger() || {};

  // state
  const [addCreditCardDialogOpen, setAddCreditCardDialogOpen] = useState(false);

  // derived state
  const availableMethods =
    savedPaymentMethod &&
    !currentUser?.paymentMethods?.some(
      (method) => method.id === savedPaymentMethod.id
    )
      ? [
          ...(currentUser?.paymentMethods || []),
          {
            ...savedPaymentMethod,
            billingDetails: null,
            linkedPassenger: null,
          },
        ]
      : currentUser?.paymentMethods;
  const paymentMethodOptions = map(
    availableMethods,
    ({ id, stripeId, card, billingDetails, linkedPassenger }) => ({
      id,
      value: stripeId,
      label: enableLinkedPassenger ? (
        <PaymentMethodOption
          card={card}
          billingDetails={billingDetails}
          linkedPassenger={linkedPassenger}
        />
      ) : (
        getPaymentMethodInfoString(card)
      ),
    })
  );

  // help auto select payment method if passenger has linked card
  const defaultPaymentMethodId = useMemo(() => {
    if (!enableLinkedPassenger || !currentUser) return null;

    return getDefaultPaymentMethodId({
      trackingFrom,
      requestContext,
      currentUser,
      request,
      selectedPassenger,
    });
  }, [
    currentUser,
    requestContext,
    trackingFrom,
    enableLinkedPassenger,
    request,
    selectedPassenger,
  ]);

  useEffect(() => {
    if (paymentElementEnabled) return;
    setValue(
      "stripePaymentMethodId",
      savedPaymentMethod?.stripeId || defaultPaymentMethodId
    );
  }, [
    defaultPaymentMethodId,
    savedPaymentMethod?.stripeId,
    setValue,
    paymentElementEnabled,
  ]);

  useEffect(() => {
    setAddCreditCardDialogOpen(!!paymentElementReturn);
  }, [paymentElementReturn, operatorId, currentUser?.id]);

  return (
    <>
      {enableCreditCardWhenBooking && (
        <>
          <Box my={2}>
            <Typography variant="h3">Payment Information</Typography>
          </Box>
          {paymentElementEnabled ? (
            <Box mb={2}>{paymentElementForm}</Box>
          ) : creditCardOnFile ? (
            <Box mb={2}>
              <RHFSingleSelect
                name="stripePaymentMethodId"
                label="Payment Card"
                control={control}
                options={paymentMethodOptions}
                defaultValue={defaultPaymentMethodId}
              />
              <Box mt={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  color="primary"
                  onClick={() => {
                    setAddCreditCardDialogOpen(true);
                  }}
                >
                  Add a new card
                </Button>
              </Box>
            </Box>
          ) : (
            paymentElementForm || (
              <PaymentForm control={control} trackingFrom={trackingFrom} />
            )
          )}
        </>
      )}
      {currentUser && (
        <AddCreditCardDialog
          open={addCreditCardDialogOpen}
          setAddCreditCardDialogOpen={setAddCreditCardDialogOpen}
          trackingName="bookingTool_addCard"
          paymentElementContext={
            paymentElementEnabled && addCreditCardDialogOpen
              ? { operatorId, payerId: currentUser.id }
              : undefined
          }
          setStripePaymentMethodId={
            paymentElementEnabled
              ? (stripeId) => setValue("stripePaymentMethodId", stripeId)
              : undefined
          }
          onSavedPaymentMethod={
            paymentElementEnabled ? onSavedPaymentMethod : undefined
          }
          paymentElementReturn={paymentElementReturn}
          onBeforePaymentElementConfirm={onBeforePaymentElementConfirm}
          onPaymentElementReturnCleared={onPaymentElementReturnCleared}
        />
      )}
    </>
  );
}

export default PaymentMethodBlock;
